import { nanoid } from 'nanoid'
import { TableInstance } from 'react-table'
import clsx from 'clsx'
import Pagination from './Pagination'
import ShimmerRows from './ShimmerRows'

interface TableProps<T extends object> {
  isLoading: boolean
  className?: string
  tableInstance: TableInstance<T>
  onRowClick: (data: object) => void
}

function Table<T extends object>({
  isLoading,
  className,
  tableInstance,
  onRowClick
}: TableProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    columns,
    prepareRow
  } = tableInstance

  return (
    <>
      <table
        {...getTableProps()}
        className={clsx('divide-y divide-gray-200 w-full', className)}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              key={`thead_tr_${nanoid()}`}
            >
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps()}
                  key={`th_${nanoid()}`}
                  scope="col"
                  className={`${column.render('Header') ? "px-4 py-3" : ""} text-xs font-medium tracking text-left text-gray-500`}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          {...getTableBodyProps()}
          className="bg-white divide-y divide-gray-200 w-full"
        >
          {page.length === 0 ? (
            <>
              {isLoading ? (
                <ShimmerRows rows={5} columns={columns.length} />
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    align="center"
                    className="py-2 text-sm"
                  >
                    No data found
                  </td>
                </tr>
              )}
            </>
          ) : (
            page.map((row) => {
              prepareRow(row)
              return (
                <tr
                  {...row.getRowProps()}
                  key={`tr_${nanoid()}`}
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => onRowClick(row.values)}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      key={`td_${nanoid()}`}
                      className={`${cell.render('Cell') ? "px-4 py-1.5" : ""} text-xs text-gray-700 whitespace-nowrap`}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              )
            })
          )}
        </tbody>
      </table>

      <Pagination<T> {...tableInstance} />
    </>
  )
}

Table.defaultProps = {
  className: null
}

export default Table
